import React from 'react';
import { FormContext, useForm } from 'react-hook-form';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useMountedSafeState } from 'features/sendouts/hooks/useMountedSafeState';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';

import { getInterviewTimeValidationError } from '../CreateSendoutDrawer/CreateSendoutDrawer.utils';
import { FORM_FIELDS_MAP } from '../CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

import { LoaderContainer } from './CreateSendoutInterviewDrawer.styled';
import { buildCreateSendoutInterviewPayload } from './CreateSendoutInterviewDrawer.utils';
import { CreateSendoutInterviewForm } from './CreateSendoutInterviewForm';

export const CreateSendoutInterviewDrawer = ({
  onClose = () => {},
  onSaved = () => {},
  sendoutId = null
}) => {
  const form = useForm();
  const { clearError, handleSubmit, setError } = form;
  const [uiState, setUIState] = useMountedSafeState({ isLoading: false });
  const { state } = useFetchWithStatusV2({
    endpoint: sendoutId ? `${Endpoints.Sendouts}/${sendoutId}` : null,
    apiVersion: 1
  });

  const isFetching = state.status === UIStatus.Loading;
  const isReady = state.status === UIStatus.Success;
  const candidateName = state.results?.candidate?.personalInformation?.full_name;
  const hiringAuthorityName = state.results?.hiringAuthority?.full_name;

  const handleSave = async formData => {
    const interviewTimeValidationError = getInterviewTimeValidationError(formData);

    if (interviewTimeValidationError) {
      setError(
        interviewTimeValidationError.fieldKey,
        'validate',
        interviewTimeValidationError.message
      );
      return;
    }

    clearError(FORM_FIELDS_MAP.InterviewFromHour.key);
    clearError(FORM_FIELDS_MAP.InterviewToHour.key);

    const payload = buildCreateSendoutInterviewPayload(formData);

    setUIState(prev => ({ ...prev, isLoading: true }));
    try {
      await onSaved({ payload });
    } finally {
      setUIState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose,
        disabled: uiState.isLoading
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: uiState.isLoading || !isReady,
        isSaving: uiState.isLoading
      },
      GridItemProps: { md: 3 },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: strings.sendoutsV2.drawers.createSendoutInterview.title }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={state.status === UIStatus.Error}>
        <EmptyPlaceholder
          subtitle={strings.formatString(strings.sendoutsV2.form.initialFetchingError, {
            documentType: 'Sendout'
          })}
          height="100%"
        />
      </When>
      <When condition={isFetching}>
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      </When>
      <When condition={isReady}>
        <FormContext {...form}>
          <CreateSendoutInterviewForm
            candidateName={candidateName}
            hiringAuthorityName={hiringAuthorityName}
          />
        </FormContext>
      </When>
    </FPDrawer>
  );
};
